import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { Loading, DeleteModal } from "../../components"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import { Search, Trash2, Pencil } from "lucide-react"
import EditContactSidebar from "./EditContactSidebar"

function Contacts() {
    const { checkAccess } = useAccessChecker()

    const [contacts, setContacts] = useState([])
    const [loading, setLoading] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [searchValue, setSearchValue] = useState("")
    const [deleteModal, setDeleteModal] = useState(false)
    const [contactIdToDelete, setContactIdToDelete] = useState(null)
    const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false)
    const [selectedContact, setSelectedContact] = useState(null)

    useEffect(() => {
        getContacts()
    }, [currentPage, searchValue])

    const getContacts = async () => {
        try {
            setLoading(true)
            const response = await axiosInstance.post(`/contacts/search?page=${currentPage}&per_page=10`, {
                searchValue: searchValue,
            })
            const responseData = response.data
            if (responseData) {
                setContacts(responseData.data)
                setCurrentPage(responseData.current_page)
                setTotalPages(responseData.last_page)
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Error fetching contacts")
        } finally {
            setLoading(false)
        }
    }

    const handleEditContact = (contact) => {
        setSelectedContact(contact)
        setIsEditSidebarOpen(true)
    }

    const handleContactUpdated = (updatedContact) => {
        setContacts((prevContacts) =>
            prevContacts.map((contact) => (contact.id === updatedContact.id ? updatedContact : contact)),
        )
        getContacts()
    }

    const handleDeleteContact = (contactId) => {
        setContactIdToDelete(contactId)
        setDeleteModal(true)
    }

    const handleSearch = (e) => {
        setSearchValue(e.target.value)
        setCurrentPage(1)
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        })
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col w-full h-full px-6 py-5 bg-neutral-50/80"
        >
            {loading && <Loading />}

            {/* Header */}
            <div className="flex lg:justify-between gap-3 lg:items-center flex-col lg:flex-row mb-6">
                <div className="flex flex-col items-start gap-1">
                    <h1 className="text-xl text-start font-semibold text-neutral-900 dark:text-white">Contacts</h1>
                    <p className="text-sm text-start text-neutral-600">Manage contacts here.</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                        <input
                            type="text"
                            placeholder="Search contacts..."
                            value={searchValue}
                            onChange={handleSearch}
                            className="pl-10 pr-4 py-2 lg:w-64 w-full text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-800 dark:text-white"
                        />
                    </div>
                </div>
            </div>

            {/* Contacts Table */}
            <div className="flex flex-col ">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden border border-neutral-200 dark:border-neutral-700 md:rounded-lg">
                            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                                <thead className="bg-neutral-50 h-14 dark:bg-neutral-800">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                                            Phone
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                                            Message
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-neutral-200 ">
                                    {contacts.map((contact) => (
                                        <tr key={contact.id} className="hover:bg-neutral-50 text-sm font-normal text-neutral-600">
                                            <td className="px-6 py-4 text-start whitespace-nowrap">
                                                {contact.firstName} {contact.lastName}
                                            </td>
                                            <td className="px-6 py-4 text-start whitespace-nowrap">{contact.email}</td>
                                            <td className="px-6 py-4 text-start whitespace-nowrap">{contact.phone}</td>
                                            <td className="px-6 text-start py-4">
                                                <div className="max-w-xs truncate">{contact.message}</div>
                                            </td>
                                            <td className="px-6 py-4 text-start whitespace-nowrap">{formatDate(contact.created_at)}</td>
                                            <td className="px-6 py-4 text-start whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    {checkAccess("contacts", "update") && (
                                                        <button
                                                            onClick={() => handleEditContact(contact)}
                                                            className="flex justify-center hover:scale-110 p-1 transition-all duration-400 items-center dark:bg-green-600 dark:bg-opacity-10 rounded-md hover:bg-neutral-200 text-neutral-500"
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                    )}
                                                    {checkAccess("contacts", "delete") && (
                                                        <button
                                                            onClick={() => handleDeleteContact(contact.id)}
                                                            className="text-red-600 hover:text-red-800 p-1 hover:bg-red-600/10 rounded-md "
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                    >
                        Next
                    </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-neutral-700">
                            Showing page <span className="font-medium">{currentPage}</span> of{" "}
                            <span className="font-medium">{totalPages}</span>
                        </p>
                    </div>
                    <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50"
                            >
                                <span className="sr-only">Previous</span>
                                <svg
                                    className="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50"
                            >
                                <span className="sr-only">Next</span>
                                <svg
                                    className="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            {/* Edit Contact Sidebar */}
            <EditContactSidebar
                isOpen={isEditSidebarOpen}
                onClose={() => setIsEditSidebarOpen(false)}
                contact={selectedContact}
                onContactUpdated={handleContactUpdated}
            />

            {/* Delete Modal */}
            <DeleteModal
                open={deleteModal}
                setOpen={setDeleteModal}
                DeleteId={contactIdToDelete}
                setDeleteId={setContactIdToDelete}
                Model="contacts"
                setDone={() => getContacts()}
                setLoding={setLoading}
            />
        </motion.div>
    )
}

export default Contacts

